import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import DashboardLayout from "../../../components/DashboardLayout/DasboardLayout";
import { API_URL } from "../../../components/config";

const formatNumber = (value) => {
  const n = typeof value === "string" ? Number(value) : value;
  if (n === null || n === undefined || !Number.isFinite(n)) return value ?? "—";
  return n.toLocaleString();
};

const formatMoney = (value) => {
  const n = typeof value === "string" ? Number(value) : value;
  if (n === null || n === undefined || !Number.isFinite(n)) return value ?? "—";
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const asNumber = (value) => {
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
};

const Card = ({ title, value, sub, className = "" }) => {
  return (
    <div className={`rounded-xl bg-white border border-gray-200 p-4 ${className}`.trim()}>
      <div className="text-xs uppercase tracking-wide text-gray-500">{title}</div>
      <div className="mt-1 text-2xl font-semibold text-gray-900">{value}</div>
      {sub ? <div className="mt-1 text-xs text-gray-500">{sub}</div> : null}
    </div>
  );
};

const Section = ({ title, right, children }) => {
  return (
    <div className="bg-white border border-gray-200 rounded-xl">
      <div className="px-4 py-3 border-b border-gray-200 flex items-center gap-3">
        <div className="font-semibold text-gray-900">{title}</div>
        <div className="ml-auto text-xs text-gray-500">{right}</div>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
};

const StatRow = ({ label, value }) => {
  return (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 last:border-b-0">
      <div className="text-sm text-gray-600">{label}</div>
      <div className="text-sm font-semibold text-gray-900">{value}</div>
    </div>
  );
};

const readJsonSafe = async (res) => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { message: text };
  }
};

const ApplicationStatistics = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);

  const pollRef = useRef(null);
  const abortRef = useRef(null);

  const fetchLatest = useCallback(async () => {
    setError(null);

    try {
      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      const res = await fetch(`${API_URL}/application_statistics/get_last_statistic`, {
        method: "GET",
        signal: controller.signal,
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = await readJsonSafe(res);
      if (!res.ok) {
        throw new Error(data?.message || `HTTP ${res.status}`);
      }

      const record = Array.isArray(data) ? data[0] : data;
      setStats(record || null);
      setLastUpdatedAt(new Date());
    } catch (e) {
      if (e?.name === "AbortError") return;
      setError(e?.message || "Failed to load application statistics.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLatest();
    pollRef.current = setInterval(fetchLatest, 15000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (abortRef.current) abortRef.current.abort();
    };
  }, [fetchLatest]);

  const tripsChart = useMemo(() => {
    const t = stats?.trips || {};
    const privateCount = asNumber(t?.private?.count);
    const rideCount = asNumber(t?.rideshare?.count);
    const privateBilled = asNumber(t?.private?.billed);
    const rideBilled = asNumber(t?.rideshare?.billed);
    return [
      { name: "Private", trips: privateCount, billed: privateBilled },
      { name: "Rideshare", trips: rideCount, billed: rideBilled },
    ];
  }, [stats]);

  const tripsPie = useMemo(() => {
    const t = stats?.trips || {};
    const privateCount = asNumber(t?.private?.count);
    const rideCount = asNumber(t?.rideshare?.count);
    return [
      { name: "Private", value: privateCount },
      { name: "Rideshare", value: rideCount },
    ];
  }, [stats]);

  const walletsTotals = stats?.wallets?.totals || {};
  const userActivity = stats?.user_activity || {};
  const complaints = stats?.complaints || {};
  const newCustomers = stats?.new_customers || {};
  const newDrivers = stats?.new_drivers || {};

  const headerRight = lastUpdatedAt
    ? `Last updated: ${lastUpdatedAt.toLocaleTimeString()}`
    : "";

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-[#002966]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto p-4 md:p-6">
          <div className="flex items-start gap-3 flex-wrap">
            <div>
              <div className="text-2xl font-bold text-[#002966]">Live App Statistics</div>
              <div className="text-sm text-gray-600">
                Endpoint: <code>/application_statistics/get_last_statistic</code>
              </div>
            </div>

            <div className="ml-auto flex items-center gap-2">
              <div className="text-xs text-gray-500">{headerRight}</div>
              <button
                type="button"
                onClick={fetchLatest}
                className="px-3 py-2 rounded bg-[#002966] text-white text-sm"
              >
                Refresh
              </button>
            </div>
          </div>

          {error ? (
            <div className="mt-4 p-3 rounded border border-red-200 bg-red-50 text-red-700 text-sm">
              {error}
            </div>
          ) : null}

          {!stats ? (
            <div className="mt-6 p-4 rounded bg-white border border-gray-200 text-gray-700">
              No statistics available.
            </div>
          ) : (
            <>
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2 }}
                className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
              >
                <Card title="Total Trips" value={formatNumber(stats.total_trips)} sub={String(stats.date || "")} />
                <Card title="Total Billed" value={formatMoney(stats.total_billed)} sub="All trips billed" />
                <Card title="Trips / Customer" value={formatNumber(stats.trips_per_customer)} sub="Average trips per customer" />
                <Card title="Avg Trip Rate" value={formatMoney(stats.average_trip_rate_usd)} sub="USD" />
              </motion.div>

              <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                <Section title="Trips Breakdown" right={headerRight}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div style={{ width: "100%", height: 260 }}>
                      <ResponsiveContainer>
                        <BarChart data={tripsChart} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="trips" fill="#002966" name="Trips" />
                          <Bar dataKey="billed" fill="#ffc000" name="Billed" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>

                    <div style={{ width: "100%", height: 260 }}>
                      <ResponsiveContainer>
                        <PieChart>
                          <Tooltip />
                          <Legend />
                          <Pie
                            data={tripsPie}
                            dataKey="value"
                            nameKey="name"
                            outerRadius={90}
                            fill="#002966"
                            label
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Private</div>
                      <StatRow label="Trips" value={formatNumber(stats?.trips?.private?.count)} />
                      <StatRow label="Billed" value={formatMoney(stats?.trips?.private?.billed)} />
                      <StatRow label="% of Trips" value={`${stats?.trips?.private?.percentage_of_total ?? "—"}%`} />
                      <StatRow label="% of Billed" value={`${stats?.trips?.private?.billed_percentage ?? "—"}%`} />
                    </div>

                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Rideshare</div>
                      <StatRow label="Trips" value={formatNumber(stats?.trips?.rideshare?.count)} />
                      <StatRow label="Billed" value={formatMoney(stats?.trips?.rideshare?.billed)} />
                      <StatRow label="% of Trips" value={`${stats?.trips?.rideshare?.percentage_of_total ?? "—"}%`} />
                      <StatRow label="% of Billed" value={`${stats?.trips?.rideshare?.billed_percentage ?? "—"}%`} />
                    </div>
                  </div>
                </Section>

                <Section title="New Users" right={headerRight}>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Customers</div>
                      <StatRow label="Total" value={formatNumber(newCustomers.total)} />
                      <StatRow label="Private" value={formatNumber(newCustomers.private)} />
                      <StatRow label="Rideshare" value={formatNumber(newCustomers.rideshare)} />
                    </div>
                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Drivers</div>
                      <StatRow label="Total" value={formatNumber(newDrivers.total)} />
                      <StatRow label="Private" value={formatNumber(newDrivers.private)} />
                      <StatRow label="Rideshare" value={formatNumber(newDrivers.rideshare)} />
                    </div>
                  </div>
                </Section>

                <Section title="User Activity & Complaints" right={headerRight}>
                  <div className="grid grid-cols-1 gap-4">
                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Activity</div>
                      <StatRow label="Signed up today" value={formatNumber(userActivity.signed_up_today)} />
                      <StatRow label="Logged in today" value={formatNumber(userActivity.logged_in_today)} />
                      <StatRow label="Logged in 2 days ago" value={formatNumber(userActivity.logged_in_2_days_ago)} />
                      <StatRow label="Logged in 30 days ago" value={formatNumber(userActivity.logged_in_30_days_ago)} />
                      <StatRow label="Avg engagement days" value={formatNumber(userActivity.average_engagement_days)} />
                    </div>

                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Complaints</div>
                      <StatRow label="Total" value={formatNumber(complaints.total)} />
                      <StatRow label="Private" value={formatNumber(complaints.private)} />
                      <StatRow label="Rideshare" value={formatNumber(complaints.rideshare)} />
                    </div>
                  </div>
                </Section>
              </div>

              <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Section title="Wallets (Driver/Customer/Company)" right={headerRight}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Driver Wallet</div>
                      <StatRow label="Open balance" value={formatMoney(stats?.wallets?.driver?.open_balance)} />
                      <StatRow label="Top up" value={formatMoney(stats?.wallets?.driver?.top_up)} />
                      <StatRow label="Billed charges" value={formatMoney(stats?.wallets?.driver?.billed_charges)} />
                      <StatRow label="Withdrawals" value={formatMoney(stats?.wallets?.driver?.withdrawals)} />
                      <StatRow label="Close balance" value={formatMoney(stats?.wallets?.driver?.close_balance)} />
                    </div>

                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Customer Wallet</div>
                      <StatRow label="Open balance" value={formatMoney(stats?.wallets?.customer?.open_balance)} />
                      <StatRow label="Billed charges" value={formatMoney(stats?.wallets?.customer?.billed_charges)} />
                      <StatRow label="Withdrawals" value={formatMoney(stats?.wallets?.customer?.withdrawals)} />
                      <StatRow label="Promo" value={formatMoney(stats?.wallets?.customer?.promo)} />
                      <StatRow label="Close balance" value={formatMoney(stats?.wallets?.customer?.close_balance)} />
                    </div>

                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Company Wallet</div>
                      <StatRow label="Open balance" value={formatMoney(stats?.wallets?.company?.open_balance)} />
                      <StatRow label="Income from charges" value={formatMoney(stats?.wallets?.company?.income_from_charges)} />
                      <StatRow label="Promotions" value={formatMoney(stats?.wallets?.company?.promotions)} />
                      <StatRow label="Withdraws out" value={formatMoney(stats?.wallets?.company?.withdraws_out)} />
                      <StatRow label="Close balance" value={formatMoney(stats?.wallets?.company?.close_balance)} />
                    </div>
                  </div>
                </Section>

                <Section title="Wallet Totals" right={headerRight}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Balances</div>
                      <StatRow label="User wallet total" value={formatMoney(walletsTotals.user_wallet_total_balance)} />
                      <StatRow label="Main wallet total" value={formatMoney(walletsTotals.main_wallet_total_balance)} />
                      <StatRow label="Revenue wallet total" value={formatMoney(walletsTotals.revenue_wallet_total_balance)} />
                      <StatRow label="Vendor wallet total" value={formatMoney(walletsTotals.vendor_wallet_total_balance)} />
                      <StatRow label="Escrow total" value={formatMoney(walletsTotals.escrow_total_balance)} />
                    </div>

                    <div className="rounded-lg bg-gray-50 border border-gray-200 p-3">
                      <div className="text-sm font-semibold text-gray-900 mb-2">Flows</div>
                      <StatRow label="Top up (user wallet)" value={formatMoney(walletsTotals.sum_top_up_uw)} />
                      <StatRow label="Withdrawals (user wallet)" value={formatMoney(walletsTotals.sum_withdrawal_uw)} />
                      <StatRow label="Commission (user wallet)" value={formatMoney(walletsTotals.sum_commission_uw)} />
                      <StatRow label="Promotions (user wallet)" value={formatMoney(walletsTotals.sum_promotion_uw)} />
                      <StatRow label="Mischief penalty (user wallet)" value={formatMoney(walletsTotals.sum_mischief_penalty_uw)} />
                    </div>
                  </div>
                </Section>
              </div>
            </>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ApplicationStatistics;
